/**
 * Query Client Configuration
 * 
 * Configures TanStack Query with Convex integration and sensible defaults.
 * 
 * Key features:
 * - Convex reactive query integration
 * - Optimized stale/cache times
 * - Error handling
 * - Retry logic
 * - SSR support
 */

import {
  
  MutationCache,
  QueryClient
} from '@tanstack/react-query'
import type {DefaultOptions} from '@tanstack/react-query';
import type { ConvexQueryClient } from '@convex-dev/react-query'

/**
 * Default query options for the application.
 * These can be overridden per-query as needed.
 */
export const DEFAULT_QUERY_OPTIONS: DefaultOptions = {
  queries: {
    // Convex queries are reactive and update automatically,
    // so we can use longer stale times
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
    
    // Retry configuration
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (client errors)
      if (error instanceof Error && 'status' in error) {
        const status = (error as any).status
        if (status >= 400 && status < 500) {
          return false
        }
      }
      // Retry up to 3 times for other errors
      return failureCount < 3
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    // Refetch configuration
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
  },
  mutations: {
    // Retry mutations once on network errors
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('network')) {
        return failureCount < 1
      }
      return false
    },
  },
}

/**
 * Creates a mutation cache with global error handling.
 */
export function createMutationCache() {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Log mutation errors for debugging
      console.error('Mutation error:', {
        error,
        mutationKey: mutation.options.mutationKey,
      })
      
      // You can add global error handling here, e.g.:
      // - Show toast notifications
      // - Track errors in PostHog
      // - Redirect on auth errors
    },
  })
}

/**
 * Creates and configures a QueryClient with Convex integration.
 * 
 * @param convexQueryClient - The Convex query client for reactive queries
 * @returns Configured QueryClient instance
 */
export function createQueryClient(convexQueryClient: ConvexQueryClient): QueryClient {
  const queryClient = new QueryClient({
    defaultOptions: {
      ...DEFAULT_QUERY_OPTIONS,
      queries: {
        ...DEFAULT_QUERY_OPTIONS.queries,
        // Integrate Convex query functions
        queryKeyHashFn: convexQueryClient.hashFn(),
        queryFn: convexQueryClient.queryFn(),
      },
    },
    mutationCache: createMutationCache(),
  })

  // Connect Convex reactive queries
  convexQueryClient.connect(queryClient)

  return queryClient
}

/**
 * Query key factories for consistent query key generation.
 * 
 * Pattern: [domain, ...identifiers, ...filters]
 * 
 * Examples:
 * - ['users', 'me'] - Current user
 * - ['tasks', { status: 'pending' }] - Filtered tasks
 * - ['tasks', taskId] - Single task
 */
export const queryKeys = {
  // User queries
  users: {
    all: ['users'] as const,
    me: () => [...queryKeys.users.all, 'me'] as const,
    profile: (userId: string) => [...queryKeys.users.all, userId] as const,
  },
  
  // Task queries
  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (filters: Record<string, any>) => 
      [...queryKeys.tasks.lists(), filters] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (taskId: string) => 
      [...queryKeys.tasks.details(), taskId] as const,
  },
  
  // Recurring task queries
  recurring: {
    all: ['recurring'] as const,
    lists: () => [...queryKeys.recurring.all, 'list'] as const,
    list: (filters: Record<string, any>) => 
      [...queryKeys.recurring.lists(), filters] as const,
    details: () => [...queryKeys.recurring.all, 'detail'] as const,
    detail: (patternId: string) => 
      [...queryKeys.recurring.details(), patternId] as const,
  },
  
  // Asset queries (placeholder for Phase 2)
  assets: {
    all: ['assets'] as const,
    catalog: () => [...queryKeys.assets.all, 'catalog'] as const,
    inventory: () => [...queryKeys.assets.all, 'inventory'] as const,
    inventoryList: (filters: Record<string, any>) => 
      [...queryKeys.assets.inventory(), filters] as const,
    detail: (assetId: string) => 
      [...queryKeys.assets.all, assetId] as const,
  },
  
  // Notification queries (placeholder for Phase 4)
  notifications: {
    all: ['notifications'] as const,
    preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
    subscriptions: () => [...queryKeys.notifications.all, 'subscriptions'] as const,
  },
} as const

/**
 * Type-safe query option factory helpers.
 * Use these to create consistent query options across the app.
 */
export type QueryKeyFactory = typeof queryKeys

/**
 * Helper to invalidate all queries for a domain.
 * 
 * @example
 * invalidateDomain(queryClient, queryKeys.tasks.all)
 */
export function invalidateDomain(
  queryClient: QueryClient,
  queryKey: ReadonlyArray<unknown>
) {
  return queryClient.invalidateQueries({ queryKey })
}

/**
 * Helper to prefetch a query.
 * Useful for optimistic navigation.
 * 
 * @example
 * await prefetchQuery(queryClient, {
 *   queryKey: queryKeys.tasks.detail(taskId),
 *   queryFn: () => convex.query(api.tasks.get, { id: taskId })
 * })
 */
export async function prefetchQuery(
  queryClient: QueryClient,
  options: {
    queryKey: ReadonlyArray<unknown>
    queryFn: () => Promise<any>
  }
) {
  return queryClient.prefetchQuery(options)
}
